/**
 * Pill tab strip, as used by the Vault (Overview / Favorites / Downloads /
 * Recently Used) and the pack drawer's resource tabs.
 *
 * Styles verbatim. Adds role="tablist" and aria-selected, which the original pill
 * buttons lacked — selection was conveyed by colour alone, so assistive tech had
 * no way to report which tab was current.
 */
import { c } from '../../styles/theme';

export interface Tab<T extends string = string> {
  key: T;
  label: string;
}

export interface TabBarProps<T extends string = string> {
  tabs: readonly Tab<T>[];
  active: T;
  onSelect: (key: T) => void;
  /** Accessible name for the strip, e.g. "Vault sections". */
  label: string;
  /** Allows horizontal scrolling, which the pack drawer's long strip needs. */
  scrollable?: boolean;
  /**
   * The pack drawer's slightly smaller pills.
   *
   * The two strips are not the same size in the prototype — the drawer's are
   * 11px/12px-padded against the page-level 12px/14px. Kept as a variant rather
   * than unified, because unifying them would visibly change the drawer.
   */
  compact?: boolean;
  marginBottom?: number;
}

export default function TabBar<T extends string = string>({
  tabs,
  active,
  onSelect,
  label,
  scrollable,
  compact,
  marginBottom,
}: TabBarProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={label}
      style={{
        display: 'flex',
        gap: 6,
        marginBottom,
        ...(scrollable
          ? { overflowX: 'auto', paddingBottom: compact ? 2 : 4 }
          : { flexWrap: 'wrap' }),
      }}
    >
      {tabs.map((t) => {
        const selected = active === t.key;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onSelect(t.key)}
            style={{
              flexShrink: scrollable ? 0 : undefined,
              fontSize: compact ? 11 : 12,
              padding: compact ? '6px 12px' : '6px 14px',
              borderRadius: 999,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              border: selected ? `1px solid ${c.accentBorder}` : `1px solid ${c.border}`,
              background: selected ? c.accentSoft : 'transparent',
              color: selected ? c.accent : c.text2,
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
